import { useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { CheckCircle, Hourglass } from '@phosphor-icons/react'
import { formatInTimeZone } from 'date-fns-tz'
import { useTranslation } from 'react-i18next'

import TextAreaField from '../../FormLegacy/FormFieldsV2/TextAreaField'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import api from '../../../api/api'
import { selectActiveBuilding } from '../../../reducers/buildingSelectorReducer'

import { serviceOptions, platformInfo } from './helpers'
import Stars from './Stars'
import Reply from './Reply'

const CHAR_LIMIT = 350

const Review = ({ isRecent, review, refetchReviews = () => {} }) => {
  const { t } = useTranslation(['organisation', 'common'])
  const selectedBuilding = useSelector(selectActiveBuilding)
  const selectedBuildingTimezone = selectedBuilding.timezone

  const [showReplyField, setShowReplyField] = useState(false)
  const [reply, setReply] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { displaySnackbar } = useSnackbar()

  const provider = review.integrationId?.provider

  const isReplied = !!review.replies.length
  const shouldShowImmediateResponseNeededLabel = !isReplied && review.immediateResponse
  const shouldShowInReviewLabel =
    provider === 'apartments' && review.replies.some((reply) => !reply.verified)

  const providerIcon = serviceOptions.find((s) => s.value === provider)?.iconPng
  const multipleRepliesNotAllowed = !platformInfo[provider]?.allowMultipleReplies
  const editingRepliesNotAllowed = !platformInfo[provider]?.allowEditingReplies
  const hideReplyButton = showReplyField || (multipleRepliesNotAllowed && isReplied)

  const replyReview = useCallback(async () => {
    try {
      setIsLoading(true)
      await api.postReputationReviewReply(review.id, reply)
      setShowReplyField(false)
      setReply('')
      refetchReviews()
      displaySnackbar(t('organisation:reputationManagement.review.toasts.replySent'), false)
    } catch (error) {
      displaySnackbar(t('organisation:reputationManagement.review.errors.replySendFailed'), true)
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }, [review, reply])

  return (
    <div className={`review ${isRecent ? 'recent' : ''}`}>
      <div className="review__header">
        <img src={providerIcon} />
        <div className="review__info">
          <Stars starsValue={0} starsTotal={review.rating} />
          {`${review.author} | ${formatInTimeZone(
            review.reviewDate,
            selectedBuildingTimezone,
            'MM/dd/yyyy hh:mm a',
          )}`}
        </div>
        {shouldShowInReviewLabel && (
          <div className="review__label">
            {t('organisation:reputationManagement.review.inReview')}
            <Hourglass size={20} />
          </div>
        )}
        {isReplied && (
          <div className="review__label">
            {t('organisation:reputationManagement.review.replied')}
            <CheckCircle size={20} />
          </div>
        )}
        {shouldShowImmediateResponseNeededLabel && (
          <div className="review__label review__label--critical">
            {t('organisation:reputationManagement.filters.immediateResponseLabel')}
          </div>
        )}
      </div>
      {review.comment && <div className="review__text">{review.comment}</div>}
      {showReplyField ? (
        <div className="review__reply-wrap">
          <TextAreaField
            name="reply"
            onChange={({ target: { value } }) => {
              if (value.length > CHAR_LIMIT) {
                setReply(value.substring(0, CHAR_LIMIT))
                return
              }

              setReply(value)
            }}
            value={reply}
            placeholder={t('organisation:reputationManagement.review.replyPlaceholder')}
            additionalClass="review__reply-field"
          />
          <div className="review__buttons-wrap">
            <button
              className="button button-gray"
              onClick={() => {
                setShowReplyField(false)
                setReply('')
              }}
            >
              {t('common:actions.cancel')}
            </button>
            <button
              className={`button button-primary ${isLoading ? 'is-loading' : ''}`}
              onClick={replyReview}
              disabled={isLoading}
            >
              {t('organisation:reputationManagement.review.replyButton')}
            </button>
          </div>
        </div>
      ) : isReplied ? (
        <div className="review__replies">
          {review?.replies?.map((reply) => (
            <Reply
              key={reply.id}
              reply={reply}
              editingRepliesNotAllowed={editingRepliesNotAllowed}
              refetchReviews={refetchReviews}
            />
          ))}
          {!hideReplyButton && (
            <button
              className="button button-primary review__btn"
              onClick={() => setShowReplyField(true)}
            >
              {t('organisation:reputationManagement.review.replyButton')}
            </button>
          )}
        </div>
      ) : (
        !hideReplyButton && (
          <button
            className="button button-primary review__btn"
            onClick={() => setShowReplyField(true)}
          >
            {t('organisation:reputationManagement.review.replyButton')}
          </button>
        )
      )}
    </div>
  )
}

Review.propTypes = {
  isRecent: PropTypes.bool,
  review: PropTypes.object,
  refetchReviews: PropTypes.func,
}

export default Review
